import timingCatalogue from './speechTimingCatalogue.json'

export interface SpeechCue {
  start: number
  end: number
}
const spoken = (text: string) =>
  text
    .replace(/\[[^\]]*\]/g, '')
    .replace(/[’']/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
const swear =
  /^(?:fuck(?:ing|ed|er|ers|s)?|motherfuck(?:ing|er|ers)?|shit(?:ty|ting|head|coin|coins|s)?|bullshit|bloody|bitch(?:es)?|bastards?|arse(?:hole)?s?|assholes?|dick(?:head)?s?|cocksuck(?:ing|in|er)?|piss(?:ing|ed)?|screw|damn(?:ed)?)$/i

export function isSwearWord(word: string): boolean {
  return swear.test(word.replace(/[^a-z]/gi, ''))
}

/** Saved performances use offline alignment. Unaligned dialogue uses a bounded pacing estimate. */
export function speechGestureCues(text: string, duration: number): readonly SpeechCue[] {
  if (!Number.isFinite(duration) || duration <= 0) return []
  const clean = spoken(text)
  const known = timingCatalogue.find(
    (entry) => spoken(entry.spokenText) === clean && Math.abs(duration - entry.durationMs / 1000) < 0.12,
  )
  if (known) return known.swearTimings.map((word) => ({ start: word.startMs / 1000, end: word.endMs / 1000 }))
  const tokens = clean.match(/[a-z]+|\p{Script=Han}|[.!?。！？,，;；]/gu) ?? []
  const weight = (token: string) =>
    /^[a-z]/.test(token)
      ? 0.65 + token.length * 0.1
      : /\p{Script=Han}/u.test(token)
        ? 0.8
        : /[.!?。！？]/.test(token)
          ? 0.7
          : 0.28
  const total = tokens.reduce((sum, token) => sum + weight(token), 0)
  let cursor = 0
  const cues: SpeechCue[] = []
  for (const token of tokens) {
    const start = (cursor / total) * duration
    cursor += weight(token)
    if (isSwearWord(token)) cues.push({ start, end: (cursor / total) * duration })
  }
  return cues
}

/** Anticipation raises the hand before the stressed word; a short hold keeps the sign legible. */
export function speechGestureAt(cues: readonly SpeechCue[], seconds: number): number {
  if (!Number.isFinite(seconds) || seconds < 0) return 0
  for (const cue of cues) {
    if (seconds >= Math.max(0, cue.start - 0.38) && seconds <= cue.end + 0.5) return 1
  }
  return 0
}
